<template>
    <div class="workflows__edit_form">
        <form-text
            :label="'Name'"
            :name="'workflows-edit-form-name'"
            :required="true"
            :v-validate="'required'"
            :data-vv-as="'name'"
            v-model="workflowName"
        ></form-text>
        <form-textarea
            :label="'Description'"
            :name="'workflows-edit-form-description'"
            :required="true"
            :v-validate="'required'"
            :data-vv-as="'description'"
            v-model="workflowDescription"
        ></form-textarea>
        <form-radio-horizontal
            :class="'workflows__edit_form_targettype'"
            :label="'When learners from'"
            :hint-position-top="true"
            :name="'workflows-edit-form-targettype'"
            :required="true"
            :v-validate="'required'"
            :data-vv-as="'type'"
            :items="[
                {
                    name: 'Organisation unit',
                    value: 'organisationUnits'
                },
                {
                    name: 'Group',
                    value: 'groups'
                }
            ]"
            v-model="workflowTargetType"
        >Select your groups or organisation units as the target audience for the workflow.
        </form-radio-horizontal>
        <template v-if="workflowTargetType === 'organisationUnits'">
            <form-select-org-units
                :class="'workflows__edit_form_targetaudience'"
                :label="''"
                :name="'workflows-edit-form-targetaudience-orgunits'"
                :value="workflowTargetAudience"
                :workspace-id="loggedInUserWorkspaceId"
                :v-validate="'required'"
                :data-vv-as="'target'"
                :required="true"
                @input="setWorkflowTargetAudience"
            ></form-select-org-units>
        </template>
        <template v-if="workflowTargetType === 'groups'">
            <form-select
                :class="'workflows__edit_form_targetaudience'"
                :label="''"
                :advanced-search-active="false"
                :get-items-getter-mapped="'getLmsSuperadminGroupsMappedLabelValue'"
                :get-items-dispatch="'lmsSuperadminGroupsGet'"
                :get-items-dispatch-data="loggedInUserWorkspaceId"
                :required="true"
                :name="'workflows-edit-form-targetaudience-groups'"
                :value="workflowTargetAudience"
                :multiple="true"
                :v-validate="'required'"
                :data-vv-as="'target'"
                @input="setWorkflowTargetAudience"
            ></form-select>
        </template>
        <form-select
            :data-vv-as="'courses'"
            :get-pagination-getter="'getEbsCoursesPagination'"
            :get-items-getter="'getEbsCoursesMappedIdNameStatusExternalTrainings'"
            :get-items-getter-mapped="'getEbsCoursesMappedLabelValueExternalTrainings'"
            :get-items-dispatch="'ebsCoursesGetAdmin'"
            :get-items-dispatch-data="{ externalTrainings: 1 }"
            :search-queryable="true"
            :table-labels="[
                {
                    key: 'name',
                    title: 'Name',
                    type: 'string',
                    sortable: true,
                    queryable: true
                },
                {
                    key: 'externalTrainings',
                    title: 'External records',
                    type: 'array'
                }
            ]"
            :item-name="['course', 'courses']"
            :label="'Have completed'"
            :name="'workflows-edit-form-trigger'"
            :hint-position-top="true"
            :prevent-clear="true"
            :required="true"
            :value="workflowTrigger"
            :v-validate="{ required: true }"
            @input="setWorkflowTrigger"
            @input-table="setWorkflowTrigger"
        >Select a course to trigger the workflow.
        </form-select>
        <form-number-radio-horizontal
            :label="'After'"
            :name="'workflows-edit-form-after'"
            :items="[
                {
                    name: 'Weeks',
                    value: 'week'
                },
                {
                    name: 'Days',
                    value: 'day'
                }
            ]"
            :required="true"
            :data-vv-as="'period type'"
            :value-number="workflowInterval"
            :value-radio="workflowIntervalType"
            :v-validate="'required|min_value:0'"
            :hint-position-top="true"
            @emit-value-number="setWorkflowInterval"
            @emit-value-radio="setWorkflowIntervalType"
        >Select a time after the completion you wish the enrolment to be made.</form-number-radio-horizontal>
        <form-select
            :data-vv-as="'courses'"
            :get-pagination-getter="'getEbsCoursesPagination'"
            :get-items-getter="'getEbsCoursesMappedIdNameStatusLabels'"
            :get-items-getter-mapped="'getEbsCoursesMappedLabelValue'"
            :get-items-dispatch="'ebsCoursesGetAdmin'"
            :search-queryable="true"
            :table-labels="[
                {
                    key: 'name',
                    title: 'Name',
                    type: 'string',
                    sortable: true,
                    queryable: true
                }
            ]"
            :item-name="['course', 'courses']"
            :label="'Enrol to'"
            :multiple="true"
            :name="'workflows-edit-form-trigger'"
            :hint-position-top="true"
            :required="true"
            :value="workflowEnrolTo"
            :v-validate="{ required: true }"
            @input="setWorkflowEnrolTo"
            @input-table="setWorkflowEnrolTo"
        >Select a course learners will be enrolled to.
        </form-select>
        <form-checkbox
            :label="'Run this rule on all users with completions'"
            :name="'workflows-edit-form-retroactive'"
            v-model="workflowRetroactive"
        >This rule will run on all users who have completed the trigger course outside of the enrolment interval.</form-checkbox>
        <form-checkbox
            :label="'Status'"
            :name="'workflows-edit-form-status'"
            :switcher="true"
            v-model="workflowEnabled"
        ></form-checkbox>
        <pop-notify
            ref="popnotifyvalidation"
            :class="'pop__notify--validation'"
            :title-text="`Something's not quite right`"
            :content-text="'Please check that you have filled out all the fields in the form correctly and try again.'"
            :cancel-button-text="'Ok'"
            :submit-button-active="false"
        />
    </div>
</template>

<script>
import PopNotify from '@/components/default/shared/PopNotify'
import { validateBeforeRouteLeave, watchContentChangeEmit } from '@/helpers/mixins'
import FormText from '@/components/default/forms/FormText'
import FormTextarea from '@/components/default/forms/FormTextarea'
import FormRadioHorizontal from '@/components/default/forms/FormRadioHorizontal'
import FormSelect from '@/components/default/forms/FormSelect'
import { User } from '@/User'
import FormNumberRadioHorizontal from '@/components/default/forms/FormNumberRadioHorizontal'
import FormCheckbox from '@/components/default/forms/FormCheckbox'
import FormSelectOrgUnits from '@/components/default/forms/FormSelectOrgUnits'

export default {
  components: {
    FormSelectOrgUnits,
    FormCheckbox,
    FormNumberRadioHorizontal,
    FormSelect,
    FormRadioHorizontal,
    FormTextarea,
    FormText,
    PopNotify
  },
  data () {
    return {
      user: new User()
    }
  },
  name: 'lms-superadmin-workflows-edit-form',
  mixins: [validateBeforeRouteLeave, watchContentChangeEmit],
  computed: {
    workflowName: {
      get () {
        return this.$store.getters.getLmsSuperadminWorkflowName
      },
      set (value) {
        this.$store.dispatch('lmsSuperadminWorkflowSetName', value)
      }
    },
    workflowDescription: {
      get () {
        return this.$store.getters.getLmsSuperadminWorkflowDescription
      },
      set (value) {
        this.$store.dispatch('lmsSuperadminWorkflowSetDescription', value)
      }
    },
    workflowTargetType: {
      get () {
        return this.$store.getters.getLmsSuperadminWorkflowTargetType
      },
      set (value) {
        this.$store.dispatch('lmsSuperadminWorkflowSetTargetType', value)
      }
    },
    workflowTargetAudience: {
      get () {
        return this.$store.getters.getLmsSuperadminWorkflowTargetAudience(this.workflowTargetType)
      }
    },
    workflowTrigger () {
      return this.$store.getters.getLmsSuperadminWorkflowWorkflowTrigger
    },
    workflowInterval () {
      return this.$store.getters.getLmsSuperadminWorkflowInterval
    },
    workflowIntervalType () {
      return this.$store.getters.getLmsSuperadminWorkflowIntervalType
    },
    workflowEnrolTo () {
      return this.$store.getters.getLmsSuperadminWorkflowEnrolTo
    },
    workflowRetroactive: {
      get () {
        return this.$store.getters.getLmsSuperadminWorkflowRetroactive
      },
      set (value) {
        this.$store.dispatch('lmsSuperadminWorkflowSetRetroactive', value === true)
      }
    },
    workflowEnabled: {
      get () {
        return this.$store.getters.getLmsSuperadminWorkflowEnabled
      },
      set (value) {
        this.$store.dispatch('lmsSuperadminWorkflowSetEnabled', value === true)
      }
    },
    loggedInUserWorkspaceId () {
      return this.$store.getters.getAuthUserSecondStageTokenDataWorkspaceId
    }
  },
  methods: {
    setWorkflowTrigger (data) {
      this.$store.dispatch('lmsSuperadminWorkflowSetWorkflowTrigger', data)
    },
    setWorkflowTargetAudience (data) {
      this.$store.dispatch('lmsSuperadminWorkflowSetTargetAudience', { data: data, target: this.workflowTargetType })
    },
    setWorkflowInterval (data) {
      this.$store.dispatch('lmsSuperadminWorkflowSetInterval', data)
    },
    setWorkflowIntervalType (data) {
      this.$store.dispatch('lmsSuperadminWorkflowSetIntervalType', data)
    },
    setWorkflowEnrolTo (data) {
      this.$store.dispatch('lmsSuperadminWorkflowSetEnrolTo', data)
    }
  },
  inject: ['$validator']
}
</script>

<style lang="scss">
    @import "~@/assets/scss/components/ebs/workflows/_workflowsaddedit.scss";
</style>
